import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as _ from 'lodash';
import { debounceTime } from 'rxjs';
import { localizedName } from 'src/app/shared/i18n/localized-name';
import { hasStoredImage, resolveImageUrl } from 'src/app/shared/utils/image-url';
import { environment } from 'src/environments/environment';
import { SubSink } from 'subsink';
import { ColumnData } from '../../Column';
import { DefaultColumnComponent } from '../default-column/default-column.component';

@Component({
  selector: 'app-default-image-column',
  templateUrl: './default-image-column.component.html',
  styleUrls: ['./default-image-column.component.scss']
})
export class DefaultImageColumnComponent extends DefaultColumnComponent implements OnInit, AfterViewInit, OnDestroy {
  private subs = new SubSink();
  override data!: ColumnData;
  /** Never nullish: a row with no picture gets `fallbackImage()`, never an empty `[src]`. */
  imageUrl: string = environment.defaultImageMagic;
  /**
   * Names the PRODUCT, never the stored path. It used to be bound to `imageUrl`, which was
   * inert while the <img> hid behind an *ngIf for a picture-less row -- and became a screen
   * reader reading `/assets/img/default-card-image-magic.jpg` once per row the moment that
   * guard came off. Empty when no name is resolvable: an unnamed decorative image is skipped
   * by assistive tech, which is right here (the product's own column carries the name).
   */
  altText = '';
  isOpen: boolean = false;
  @ViewChild('cdkConOverlay', {static:  false}) overLayConected: CdkConnectedOverlay | undefined;

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
  
  noImage() {
    // Second line of defence: a well-formed URL that 404s. Same fallback as a row with no
    // picture at all -- see fallbackImage().
    this.imageUrl = this.fallbackImage();
  }

  /**
   * The column's own configured fallback, or the bundled card default.
   *
   * Asked in BOTH places a fallback can be needed: a row the backend has no picture for, and a
   * stored URL that is well-formed and 404s. It used to be asked in only one of them --
   * `resolveImageUrl` answers `defaultImageMagic` for a picture-less row, so once `[src]` was
   * bound unconditionally such a row could no longer reach `noImage()`, and a column carrying
   * `attributes['errorImage']` silently painted the card default while the 404 path honoured its
   * configured one. Two renderings of one decision, and nothing in-tree configures the attribute
   * today, so the disagreement would have surfaced as a bug report from whoever first did.
   */
  private fallbackImage(): string {
    return this.data.field.attributes?.['errorImage'] || environment.defaultImageMagic;
  }

  override ngOnInit(): void {
    // The stored value is gateway-relative (`/api/v2/image/mirror/...`); see resolveImageUrl.
    // A row with no picture takes this column's own fallback rather than resolveImageUrl's
    // bundled default, so the two fallbacks this component can reach cannot disagree.
    const stored = this.getValue();
    this.imageUrl = hasStoredImage(stored) ? resolveImageUrl(stored) : this.fallbackImage();
    this.altText = this.resolveAltText();
  }

  /**
   * The object that OWNS the picture is this column's field path minus its last segment --
   * `product.imageName` -> `product`, `cardData.image` -> `cardData` -- which is where the
   * name lives. A column configured with a bare field (or a row shaped otherwise) can point at
   * it explicitly with `attributes['altField']`; a path resolving to nothing answers ''.
   */
  private resolveAltText(): string {
    const field = this.data?.field;
    const explicit = field?.attributes?.['altField'];
    const path = typeof explicit === 'string' && explicit !== '' ? explicit : ownerPath(field?.field);
    const owner: any = path ? _.result(this.data.row, path) : this.data.row;
    if (owner == null || typeof owner !== 'object') {
      return '';
    }
    return localizedName(owner, this.localeService.current)
      || owner.defaultDescription
      || owner.description
      || '';
  }

  ngAfterViewInit(): void {
    this.subs.add(
      this.overLayConected?.overlayOutsideClick.pipe(debounceTime(10)).subscribe(d => {
        this.isOpen = false;
      })
    );
  }
}

/** `product.imageName` -> `product`; a single-segment path (or none) -> '' (the row itself). */
function ownerPath(field: string | undefined): string {
  if (!field) {
    return '';
  }
  const cut = field.lastIndexOf('.');
  return cut > 0 ? field.slice(0, cut) : '';
}
